import React, { useEffect, useRef, useState } from "react";
import { getDatabase, ref, onValue, update, remove } from "firebase/database";
import AppHeader from "../common/AppHeader";
import BottomNav from "../common/BottomNav";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

const BOTTOM_NAV_HEIGHT = 76;

export default function Notifications() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastNotificationId, setLastNotificationId] = useState(null);
  const isFirstLoad = useRef(true);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    if (!user.spId) return;
    const userId = user.spId;
    const db = getDatabase();
    const notificationsRef = ref(db, `notifications/${userId}`);

    const unsubscribe = onValue(notificationsRef, (snapshot) => {
      const data = snapshot.val() || {};
      const list = Object.entries(data)
        .map(([key, value]) => ({ ...value, id: key }))
        .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
      setNotifications(list);
      setLoading(false);

      if (list.length > 0 && list[0].id !== lastNotificationId) {
        if (!isFirstLoad.current) {
          const audio = new Audio(process.env.PUBLIC_URL + "/message-ringtone-magic.mp3");
          audio.play();
        }
        setLastNotificationId(list[0].id);
      } else {
        setLastNotificationId(list[0]?.id || null);
      }
      isFirstLoad.current = false;
    });

    return () => unsubscribe();
  }, [lastNotificationId]);

  const markAsRead = (notification) => {
    if (notification.isRead || !notification.id) return;
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const userId = user.spId;
    const db = getDatabase();
    update(ref(db, `notifications/${userId}/${notification.id}`), { isRead: true });
    setNotifications((prev) =>
      prev.map((n) =>
        n.id === notification.id ? { ...n, isRead: true } : n
      )
    );
  };

  const markAllAsRead = () => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const userId = user.spId;
    const db = getDatabase();
    const updates = {};
    notifications.forEach(n => {
      if (!n.isRead && n.id) {
        updates[`notifications/${userId}/${n.id}/isRead`] = true;
      }
    });
    if (Object.keys(updates).length) {
      update(ref(db), updates);
      setNotifications((prev) =>
        prev.map((n) => n.id && !n.isRead ? { ...n, isRead: true } : n)
      );
    }
  };

  const clearAllNotifications = () => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const userId = user.spId;
    const db = getDatabase();
    const notificationsRef = ref(db, `notifications/${userId}`);
    remove(notificationsRef).then(() => setNotifications([]));
  };

  const validNotifications = notifications.filter(n => n.title || n.body || n.message);

  function groupByDate(notifications) {
    const groups = {};
    notifications.forEach(n => {
      const rawDate = n.timestamp || n.time || n.createdAt;
      let d = rawDate ? new Date(rawDate) : null;
      let label = "Unknown";
      if (d && !isNaN(d)) {
        const today = new Date();
        const yesterday = new Date();
        yesterday.setDate(today.getDate() - 1);
        const dateStr = d.toLocaleDateString();
        if (dateStr === today.toLocaleDateString()) label = t("Notifications.today", "Today");
        else if (dateStr === yesterday.toLocaleDateString()) label = t("Notifications.yesterday", "Yesterday");
        else label = d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
      }
      if (!groups[label]) groups[label] = [];
      groups[label].push(n);
    });
    return groups;
  }

  const grouped = groupByDate(validNotifications);

  const dateOrder = [t("Notifications.today", "Today"), t("Notifications.yesterday", "Yesterday")];
  const sortedGroupKeys = [
    ...dateOrder.filter(label => grouped[label]),
    ...Object.keys(grouped)
      .filter(label => !dateOrder.includes(label))
      .sort((a, b) => new Date(b) - new Date(a))
  ];

  return (
    <div style={{ background: "#f7f8fa", minHeight: "100vh", fontFamily: "inherit" }}>
      <AppHeader title={t("Notifications.notifications", "Notifications")} showBack={true} />
      <div style={{ maxWidth: 430, margin: "0 auto", padding: `12px 12px ${BOTTOM_NAV_HEIGHT + 12}px 12px` }}>
        {loading ? (
          <div style={{ textAlign: "center", marginTop: 40 }}>{t("Notifications.loading", "Loading...")}</div>
        ) : validNotifications.length === 0 ? (
          <div
            style={{
              minHeight: "calc(100vh - 250px)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              color: "#888",
              fontSize: 16,
              fontWeight: 600,
              letterSpacing: 0.2,
              textAlign: "center"
            }}
          >
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: "50%",
                background: "#f7f8fa",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: 12,
                boxShadow: "0 2px 8px rgba(44,62,80,0.08)"
              }}
            >
              <svg width="24" height="24" fill="#b2b7be" viewBox="0 0 24 24">
                <path d="M12 2a7 7 0 0 1 7 7v3.5l1.29 2.58A1 1 0 0 1 19.42 17H4.58a1 1 0 0 1-.87-1.42L5 12.5V9a7 7 0 0 1 7-7zm0 18a2.5 2.5 0 0 0 2.45-2h-4.9A2.5 2.5 0 0 0 12 20z"/>
              </svg>
            </div>
            {t("Notifications.no_notifications", "No notifications yet")}
          </div>
        ) : (
          <>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginBottom: 12 }}>
              <button
                onClick={markAllAsRead}
                style={{
                  background: "#1976d2",
                  color: "#fff",
                  border: "none",
                  padding: "8px 12px",
                  fontWeight: 600,
                  fontSize: 12,
                  cursor: "pointer"
                }}
              >
                {t("Notifications.mark_all_read", "Mark All Read")}
              </button>
              <button
                onClick={clearAllNotifications}
                style={{
                  background: "#fff",
                  border: "1px solid #e0e3e7",
                  color: "#1976d2",
                  fontWeight: 600,
                  fontSize: 12,
                  padding: "8px 12px",
                  cursor: "pointer"
                }}
                type="button"
              >
                {t("Notifications.clear_all", "Clear All")}
              </button>
            </div>
            {sortedGroupKeys.map(dateLabel => (
              <div key={dateLabel}>
                <div style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  margin: "16px 0 10px 0"
                }}>
                  <span style={{
                    background: "#fff",
                    color: "#1976d2",
                    fontWeight: 600,
                    fontSize: 11,
                    padding: "4px 16px",
                    border: "1px solid #e0e3e7"
                  }}>
                    {dateLabel}
                  </span>
                </div>
                {[...grouped[dateLabel]].reverse().map((n, idx) => {
                  let dateStr = "";
                  const rawDate = n.timestamp || n.time || n.createdAt;
                  if (rawDate) {
                    const d = new Date(rawDate);
                    if (!isNaN(d)) {
                      dateStr = d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
                    } else {
                      dateStr = rawDate;
                    }
                  }
                  const actionUrl = n.data?.actionUrl || n.actionUrl;

                  return (
                    <div
                      key={n.id || idx}
                      style={{
                        background: "#fff",
                        padding: "12px",
                        marginBottom: 12,
                        boxShadow: "0 1px 4px rgba(44,62,80,0.1)",
                        border: n.isRead ? "1px solid #e0e3e7" : "2px solid #1976d2",
                        opacity: n.isRead ? 0.7 : 1,
                        cursor: actionUrl ? "pointer" : "default",
                        position: "relative"
                      }}
                      onClick={() => {
                        if (actionUrl) {
                          markAsRead(n);
                          navigate(actionUrl);
                        }
                      }}
                    >
                      <div style={{
                        fontWeight: 700,
                        color: "#232b35",
                        fontSize: 14,
                        marginBottom: 4,
                        paddingRight: n.isRead ? 0 : 70
                      }}>
                        {n.title || t("Notifications.notification", "Notification")}
                      </div>
                      <div style={{
                        color: "#444",
                        fontSize: 13,
                        marginBottom: 6,
                        lineHeight: 1.5
                      }}>
                        {n.body || n.message}
                      </div>
                      <div style={{
                        fontSize: 11,
                        color: "#888",
                        fontWeight: 500
                      }}>
                        {dateStr}
                      </div>
                      {!n.isRead && (
                        <button
                          onClick={e => {
                            e.stopPropagation();
                            markAsRead(n);
                          }}
                          style={{
                            position: "absolute",
                            top: 10,
                            right: 10,
                            background: "#1976d2",
                            color: "#fff",
                            border: "none",
                            padding: "4px 8px",
                            fontSize: 10,
                            fontWeight: 600,
                            cursor: "pointer"
                          }}
                        >
                          {t("Notifications.mark_read", "Read")}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </>
        )}
      </div>
      <BottomNav />
    </div>
  );
}